import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, PoundSterling, Timer, Flame, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useSiteConfig } from "@/lib/useSiteConfig";
import PublicHeader from "@/components/PublicHeader";
import ListingCard from "@/components/ListingCard";
import Seo, {
  breadcrumbSchema,
  faqSchema,
  localBusinessSchema,
} from "@/seo/Seo";
import { CATEGORIES, findBorough, SITE_ORIGIN } from "@/seo/data";

/**
 * One component handles ALL borough landing pages, keyed off the URL slug.
 * Each page has a unique H1, meta title, meta description, and copy body
 * built from `/seo/data.js` — Google penalises duplicate content so every
 * page needs its own distinct paragraphs (not just template swaps).
 *
 * Live inventory is pulled from the search API filtered by the borough's
 * postcode centroid so the counts and cards reflect reality. When
 * inventory is empty the page still renders a full copy body — an empty
 * page is worse than a light one for SEO, and long-tail queries still
 * bring traffic that converts on the next fresh listing.
 */
export default function BoroughLanding() {
  const { borough: slug } = useParams();
  const borough = findBorough(slug);
  const { launch_phase } = useSiteConfig();
  const prelaunch = launch_phase !== "live";
  const [listings, setListings] = useState([]);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!borough) return;
    // Use the borough centroid as a search origin proxy. This surfaces the
    // aggregated inventory nearby without exposing any single retailer.
    api
      .get("/listings/search", {
        params: {
          lat: borough.centroid.lat,
          lng: borough.centroid.lng,
          radius_km: 12,
          limit: 12,
        },
      })
      .then((r) => {
        setListings(r.data.results || []);
        setCount(r.data.count || 0);
      })
      .catch(() => {
        setListings([]);
        setCount(0);
      });
  }, [borough]);

  const faqs = useMemo(
    () => borough ? [
      {
        q: `How does Floor-Match work in ${borough.name}?`,
        a: `Enter your room size and a ${borough.name} postcode. We match your dimensions against surplus rolls, remnants and pack ends held by verified independent flooring retailers across ${borough.name}. Reserve online, collect from the retailer's shop.`,
      },
      {
        q: `What postcodes does Floor-Match cover in ${borough.name}?`,
        a: `${borough.postcodes.slice(0, 8).join(", ")}${borough.postcodes.length > 8 ? " and more" : ""}. If your postcode is on the edge of ${borough.name} we'll also show nearby stock in neighbouring boroughs.`,
      },
      {
        q: `Is there a fee to reserve in ${borough.name}?`,
        a: `You pay the retailer's advertised price at reservation — nothing on top. Floor-Match takes a small commission from the retailer, which is why remnant prices stay 30–70 % below high-street rates.`,
      },
      {
        q: `How long do I have to collect?`,
        a: `Most customers collect the same day. Once you pay online the retailer's exact address and contact number unlock instantly in your reservation email — walk in during their opening hours. We hold the stock for up to 48 hours as a safety net.`,
      },
    ] : [],
    [borough],
  );

  if (!borough) return <Navigate to="/" replace />;

  const structured = [
    localBusinessSchema(),
    breadcrumbSchema([
      { name: "Home", url: `${SITE_ORIGIN}/` },
      { name: `Flooring in ${borough.name}`, url: `${SITE_ORIGIN}/flooring/${borough.slug}` },
    ]),
    faqSchema(faqs),
  ];

  return (
    <>
      <Seo
        title={`Surplus flooring in ${borough.name} — carpet, laminate, LVT, vinyl`}
        description={`Remnants, roll ends and pack surplus from verified independent flooring retailers across ${borough.name}. Enter your room size, reserve online, collect locally. Covers ${borough.postcodes.slice(0, 4).join(", ")} and more.`}
        path={`/flooring/${borough.slug}`}
        keywords={`carpet remnants ${borough.name.toLowerCase()}, laminate flooring ${borough.name.toLowerCase()}, LVT ${borough.name.toLowerCase()}, vinyl flooring ${borough.name.toLowerCase()}, cheap flooring ${borough.name.toLowerCase()}, flooring near me ${borough.name.toLowerCase()}`}
        structured_data={structured}
      />
      <PublicHeader />
      <main data-testid="borough-landing">
        {/* Announcement bar */}
        <div className="bg-[#2A5A3B] text-white text-xs sm:text-sm">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-center gap-2 flex-wrap">
            <Flame className="w-3.5 h-3.5 text-[#ffb078]" aria-hidden="true" />
            {prelaunch ? (
              <>
                <span className="font-semibold">{borough.name} retailer registration open</span>
                <span className="hidden sm:inline text-white/70">·</span>
                <span className="hidden sm:inline">Customer launch coming soon · Local shops signing up</span>
              </>
            ) : (
              <>
                <span className="font-semibold">Fresh {borough.name} listings every day</span>
                <span className="hidden sm:inline text-white/70">·</span>
                <span className="hidden sm:inline">Reserve online · Collect same day · Local shops</span>
              </>
            )}
          </div>
        </div>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-6">
          <nav className="text-xs text-slate-500 mb-3" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-slate-800">Home</Link>
            <span className="mx-1">/</span>
            <Link to="/search" className="hover:text-slate-800">Search</Link>
            <span className="mx-1">/</span>
            <span className="text-slate-700">{borough.name}</span>
          </nav>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#2A5A3B] bg-[#2A5A3B]/10 px-3 py-1.5 rounded-full mb-4">
            <MapPin className="w-3.5 h-3.5" aria-hidden="true" /> Local to {borough.name}
          </span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight">
            Save up to <span className="text-[#E05915]">70%</span> on flooring
            <br className="hidden sm:inline" /> in {borough.name}.
          </h1>
          <p className="text-slate-600 mt-3 text-base max-w-2xl">
            {borough.tagline}. Carpet remnants, laminate pack ends, LVT surplus and sheet vinyl
            — held by verified independent {borough.name} retailers, matched to
            your exact room.
          </p>

          {/* Trust chips */}
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
              <PoundSterling className="w-3.5 h-3.5" /> 30–70 % below retail
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
              <Timer className="w-3.5 h-3.5" /> {prelaunch ? "Launching soon" : "Collect same day"}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
              <ShieldCheck className="w-3.5 h-3.5" /> Verified {borough.name} shops
            </span>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/search?lat=${borough.centroid.lat}&lng=${borough.centroid.lng}`}
              className="fm-btn-primary inline-flex items-center gap-2 text-sm"
              data-testid="borough-hero-cta"
            >
              Find flooring in {borough.name} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-slate-800 text-sm"
            >
              Enter room size first
            </Link>
          </div>

          <p className="text-slate-700 leading-relaxed mt-8 text-base">
            {borough.hero_intro}
          </p>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
          <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
            Browse by category in {borough.name}
          </h2>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="borough-category-grid">
            {CATEGORIES.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/flooring/${borough.slug}/${c.slug}`}
                  className="block bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-emerald-900 hover:shadow-sm transition"
                  data-testid={`category-link-${c.slug}`}
                >
                  <div className="font-medium text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    in {borough.name}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display font-semibold text-lg text-slate-900">
              {count > 0
                ? `${count} live listing${count === 1 ? "" : "s"} near ${borough.name} — ready to reserve`
                : `New ${borough.name} listings drop daily`}
            </h2>
            <Link
              to={`/search?lat=${borough.centroid.lat}&lng=${borough.centroid.lng}`}
              className="text-sm text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1"
              data-testid="see-all-listings-link"
            >
              See all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {listings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {listings.map((l) => (
                <ListingCard key={l.id} l={l} />
              ))}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="grid place-items-center w-11 h-11 rounded-xl bg-[#E05915]/10 text-[#E05915] shrink-0">
                <Flame className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-display font-semibold text-slate-900">
                  No live stock in {borough.name} right now
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  New remnants and pack surplus are added as local shops list them. Enter your room
                  size and we'll email you with any new matching flooring in {borough.name}.
                </p>
              </div>
              <Link
                to="/"
                className="fm-btn-primary text-sm inline-flex items-center gap-2 shrink-0"
              >
                Set an alert <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </section>

        {/* Why locals switch — salesy 3-up */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10" data-testid="borough-why-switch">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
            Why {borough.name} is switching to surplus
          </p>
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mt-1 mb-5">
            Better flooring, better price, no waiting around.
          </h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              {
                icon: <PoundSterling className="w-5 h-5" />,
                t: "Well below the high-street price",
                d: `Surplus and remnant stock is typically 30–70 % below high-street retail. As an illustrative example, a bedroom carpet at around £240 new can be about £99 on Floor-Match — carpet only; fitting and underlay extra.`,
              },
              {
                icon: <Timer className="w-5 h-5" />,
                t: "Same-day collection",
                d: `Reserve online, walk into your local ${borough.name} shop within opening hours and take it home. No long wait, no delivery slot to book.`,
              },
              {
                icon: <CheckCircle2 className="w-5 h-5" />,
                t: "Actually fits your room",
                d: "We only show stock big enough for your exact dimensions — no wasted trips.",
              },
            ].map((b) => (
              <div key={b.t} className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="grid place-items-center w-10 h-10 rounded-lg bg-[#E05915]/10 text-[#E05915]">
                  {b.icon}
                </div>
                <h3 className="font-display font-semibold text-lg text-slate-900 mt-3">{b.t}</h3>
                <p className="text-sm text-slate-600 mt-1">{b.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
          <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
            Neighbourhoods we cover
          </h2>
          <p className="text-slate-700 leading-relaxed">
            Floor-Match works across every neighbourhood in {borough.name}
            {" — "}
            {borough.neighbourhoods.join(", ")} and everywhere in between.
            Our verified retailer network holds stock throughout the borough,
            so wherever you are you're never more than a short drive from a
            surplus roll that fits your room.
          </p>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16" data-testid="borough-faq">
          <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
            {borough.name} flooring FAQs
          </h2>
          <ul className="space-y-4">
            {faqs.map((f) => (
              <li key={f.q} className="border-b border-slate-200 pb-4">
                <h3 className="font-medium text-slate-900">{f.q}</h3>
                <p className="text-sm text-slate-700 mt-1 leading-relaxed">{f.a}</p>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500 mt-6 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Every retailer on Floor-Match is verified. We hide retailer names
            and addresses until you've paid — so there's no cold-calling and
            no hard sell.
          </p>
        </section>
      </main>
    </>
  );
}
